import { Button, PageHeader, PeriodSwitcher, TOUCH } from "@nymbl/nos2-rules";
import { CalendarPlus, Check, Clock } from "lucide-react";
import { RevokeSignOff } from "@/components/time/RevokeSignOff";
import { TodayChip } from "@/components/time/TodayChip";
import { formatWeekRange } from "@/lib/time/dates";
import { cn } from "@/lib/utils";
import type { SignOffRow } from "@/schemas/time";

interface WeekHeaderProps {
  /** "YYYY-MM-DD" of the week's Sunday. */
  weekStart: string;
  isCurrentWeek: boolean;
  /** Entries in Month mode: the switcher shows this month and steps by month (the handlers do the stepping). */
  month?: { label: string; isCurrent: boolean };
  /** Ended weeks still open; null while sign-offs load or when they failed. */
  openWeekCount: number | null;
  /** Any open week, the current one included: nothing is "All signed off" while one remains. */
  hasSignable: boolean;
  /** The shown week's sign-off when it is signed off and its month is open (revocable), else null. */
  revocableWeek: SignOffRow | null;
  /** That week's month when it is signed off too: both are revoked together. */
  revocableMonth: SignOffRow | null;
  onPrev: () => void;
  onNext: () => void;
  onThisWeek: () => void;
  onSignOff: () => void;
  onLogTime: () => void;
  /**
   * From calendar: the week's meetings still to log (null = not connected yet, so no count), or
   * undefined to hide the button (no calendar to offer, or an admin impersonating).
   */
  calendarCount?: number | null;
  onCalendar: () => void;
}

/** The Time page header in the top bar: title, week switcher, sign-off status, From calendar and Log time. */
export function WeekHeader(props: WeekHeaderProps) {
  const { weekStart, isCurrentWeek, openWeekCount } = props;
  const signOffLabel =
    openWeekCount === null
      ? "Sign off"
      : openWeekCount > 0
        ? `Sign off (${openWeekCount})`
        : props.hasSignable
          ? "Sign off"
          : "All signed off";
  const calendarCount = props.calendarCount;
  const calendarLabel = calendarCount ? `From calendar (${calendarCount})` : "From calendar";
  return (
    <PageHeader
      title="Time"
      period={
        <PeriodSwitcher
          unit={props.month ? "month" : "week"}
          label={props.month ? props.month.label : formatWeekRange(weekStart)}
          isCurrent={props.month ? props.month.isCurrent : isCurrentWeek}
          onPrev={props.onPrev}
          onNext={props.onNext}
          onCurrent={props.onThisWeek}
          labelClassName="w-40 md:w-44"
        />
      }
      actions={
        <>
          {/* With Revoke too the row is full, so the chip yields. */}
          {props.revocableWeek === null && <TodayChip />}
          {props.revocableWeek && (
            <RevokeSignOff row={props.revocableWeek} month={props.revocableMonth} look="header" />
          )}
          <Button
            variant={openWeekCount === 0 ? "outline" : "default"}
            className={cn(TOUCH, openWeekCount === 0 && "text-success")}
            onClick={props.onSignOff}
          >
            <Check aria-hidden="true" />
            {signOffLabel}
          </Button>
          {calendarCount !== undefined && (
            <Button
              variant={calendarCount ? "default" : "outline"}
              className={TOUCH}
              onClick={props.onCalendar}
            >
              <CalendarPlus aria-hidden="true" />
              {calendarLabel}
            </Button>
          )}
          <Button className={TOUCH} onClick={props.onLogTime}>
            <Clock aria-hidden="true" />
            Log time
          </Button>
        </>
      }
    />
  );
}
